import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import {
  ShieldAlert, ShieldCheck, AlertTriangle, RefreshCcw, X, Monitor,
  Smartphone, Tablet, Globe, UserX, ListChecks, MapPin, Loader2,
} from "lucide-react";

/**
 * Auth Faza 4 — Security & Audit dashboard.
 *
 * Layout:
 *   • 5 overview cards (active sessions, successful 24h, failed 24h, active
 *     users, revoked count)
 *   • Active sessions table with per-row revoke (confirm dialog) +
 *     "revoke all for user" button
 *   • Failed logins table with period filter (24h / 7d / 30d)
 *   • Auth audit events table with the same period filter
 *
 * RBAC (mirrors the backend gates):
 *   • owner       → full access
 *   • admin_fped  → never reaches this tab either: it is the owner's (AdminDashboard)
 *   • viewer      → read-only (no Revoke buttons)
 *   • partner     → never reaches this tab (sidebar already hides it)
 */

const PERIODS = [
  { value: "24h", label: "Ultimele 24h" },
  { value: "7d",  label: "Ultimele 7 zile" },
  { value: "30d", label: "Ultimele 30 zile" },
];

const REASON_LABELS = {
  domain_not_allowed: "Domeniu neautorizat",
  user_not_found: "Utilizator inexistent",
  inactive_user: "Cont inactiv",
  oauth_error: "Eroare OAuth",
  email_not_verified: "Email neverificat",
  wrong_hd: "Workspace greșit",
  failed_login_attempt: "Tentativă eșuată",
  magic_link_invalid: "Magic link invalid",
  login_failed: "Eroare login (legacy)",
  login_denied: "Refuz autentificare (legacy)",
};

const ACTION_LABELS = {
  login_success: "Login reușit",
  failed_login_attempt: "Login eșuat",
  logout: "Logout",
  auth_session_revoked: "Sesiune revocată",
  auth_all_sessions_revoked: "Toate sesiunile revocate",
  magic_link_issued: "Magic link emis",
  magic_link_resent: "Magic link retrimis",
  magic_link_invalid: "Magic link invalid",
  magic_link_rate_limited: "Magic link rate-limited",
  user_invited: "Utilizator invitat",
  user_updated: "Utilizator modificat",
  user_removed: "Utilizator șters",
  bootstrap_owner_created: "Owner bootstrap creat",
  login_failed: "Eroare login (legacy)",
  login_denied: "Refuz autentificare (legacy)",
  sessions_revoked: "Sesiuni revocate (legacy)",
};

export default function SecurityTab({ currentRole, currentEmail }) {
  const isViewer = currentRole === "viewer";
  const isOwner = currentRole === "owner";
  const canRevoke = !isViewer && currentRole !== "partner_operator";

  return (
    <div className="space-y-6" data-testid="security-tab">
      <OverviewCards />
      <ActiveSessionsTable
        canRevoke={canRevoke}
        isOwner={isOwner}
        currentEmail={currentEmail}
      />
      <FailedLoginsTable />
      <AuditEventsTable />
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function useEndpoint(path) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Tick lets callers (e.g. a "Refresh" button) force a re-fetch without
  // changing `path`. Bumping it re-runs the effect below.
  const [tick, setTick] = useState(0);
  const reload = useCallback(() => setTick((n) => n + 1), []);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    api.get(path)
      .then((r) => { if (!cancelled) { setData(r.data); setError(null); } })
      .catch((err) => {
        if (cancelled) return;
        setError(err?.response?.data?.detail || err.message || "Eroare");
        setData(null);
      })
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [path, tick]);

  return { data, loading, error, reload };
}

function Card({ title, value, hint, icon: Icon, tone = "neutral", testid }) {
  const tones = {
    purple:  "bg-[#F0E7F4] border-[#965EA7]/30 text-[#7A4986]",
    green:   "bg-[#E7F2EB] border-[#519872]/30 text-[#2F6B4E]",
    red:     "bg-[#FEE2E2] border-[#E63946]/30 text-[#9b1f2c]",
    blue:    "bg-[#E5EEF5] border-[#3F8EBA]/30 text-[#1f5d80]",
    neutral: "bg-white border-[#E5E7EB] text-[#373A45]",
  };
  return (
    <div
      className={`rounded-2xl p-4 border ${tones[tone] || tones.neutral} flex items-start gap-3`}
      data-testid={testid}
    >
      {Icon && <Icon size={18} className="mt-0.5 opacity-70" />}
      <div className="flex-1">
        <div className="text-[10px] uppercase tracking-widest font-semibold opacity-70">{title}</div>
        <div className="font-display text-2xl font-bold mt-0.5">{value ?? "—"}</div>
        {hint && <div className="text-[10px] mt-0.5 opacity-70">{hint}</div>}
      </div>
    </div>
  );
}

function SectionCard({ title, icon: Icon, action, children, testid }) {
  return (
    <section
      className="bg-white rounded-2xl border border-[#E5E7EB] p-5 md:p-6"
      data-testid={testid}
    >
      <div className="flex items-start justify-between gap-3 mb-4">
        <div className="flex items-center gap-2">
          {Icon && <Icon size={18} className="text-[#965EA7]" />}
          <h3 className="font-display text-base md:text-lg font-semibold text-[#373A45]">{title}</h3>
        </div>
        {action}
      </div>
      {children}
    </section>
  );
}

function LoadingRows({ rows = 4 }) {
  return (
    <div className="space-y-2 animate-pulse">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="h-9 bg-[#F5F6F6] rounded" />
      ))}
    </div>
  );
}

function EmptyBlock({ children = "Niciun eveniment în perioada selectată." }) {
  return (
    <div className="rounded-xl border border-[#E5E7EB] bg-[#F5F6F6] p-6 text-sm text-[#6B7280] text-center">
      {children}
    </div>
  );
}

function PeriodFilter({ value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2" data-testid={`${testidPrefix}-period`}>
      {PERIODS.map((p) => (
        <button
          key={p.value}
          type="button"
          onClick={() => onChange(p.value)}
          data-testid={`${testidPrefix}-period-${p.value}`}
          className={`px-3 py-1.5 rounded-full text-xs font-semibold border transition-colors ${
            value === p.value
              ? "bg-[#965EA7] text-white border-[#965EA7]"
              : "bg-white text-[#373A45] border-[#E5E7EB] hover:border-[#965EA7]"
          }`}
        >
          {p.label}
        </button>
      ))}
    </div>
  );
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("ro-RO", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch (_e) {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diffMs = Date.now() - d.getTime();
  const mins = Math.round(diffMs / 60000);
  if (mins < 1) return "acum câteva secunde";
  if (mins < 60) return `acum ${mins} min`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `acum ${hours}h`;
  const days = Math.round(hours / 24);
  return `acum ${days} z`;
}

function DeviceIcon({ device }) {
  const I = device === "mobile" ? Smartphone : device === "tablet" ? Tablet : Monitor;
  return <I size={14} className="opacity-60" />;
}

// ---------------------------------------------------------------------------
// 1) Overview cards
// ---------------------------------------------------------------------------

function OverviewCards() {
  const { data, loading, error, reload } = useEndpoint("/admin/security/overview");
  return (
    <SectionCard
      title="Privire de ansamblu"
      icon={ShieldCheck}
      action={
        <button
          type="button"
          onClick={reload}
          className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#965EA7] hover:bg-[#F0E7F4] rounded-full px-3 py-1.5 transition-colors"
          data-testid="security-overview-refresh"
        >
          <RefreshCcw size={12} /> Reîmprospătează
        </button>
      }
      testid="security-overview"
    >
      {loading && <LoadingRows rows={2} />}
      {error && <div className="text-sm text-[#9b1f2c]">{error}</div>}
      {!loading && !error && data && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <Card title="Sesiuni active" value={data.active_sessions}
                icon={Monitor} tone="purple" testid="security-card-active-sessions" />
          <Card title="Login-uri reușite (24h)" value={data.successful_logins_24h}
                icon={ShieldCheck} tone="green" testid="security-card-success-24h" />
          <Card title="Login-uri eșuate (24h)" value={data.failed_logins_24h}
                icon={ShieldAlert} tone="red" testid="security-card-failed-24h" />
          <Card title="Utilizatori activi" value={data.active_users}
                icon={Globe} tone="blue" testid="security-card-active-users" />
          <Card title="Sesiuni revocate"   value={data.revoked_sessions}
                icon={UserX} tone="neutral" testid="security-card-revoked" />
        </div>
      )}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 2) Active sessions table + revoke
// ---------------------------------------------------------------------------

function ActiveSessionsTable({ canRevoke, isOwner, currentEmail }) {
  const [statusFilter, setStatusFilter] = useState("active");
  const path = `/admin/security/sessions?status=${statusFilter}&limit=200`;
  const { data, loading, error, reload } = useEndpoint(path);

  const [revokingId, setRevokingId] = useState(null);
  const [pendingDialog, setPendingDialog] = useState(null);

  const doRevoke = useCallback(async (sess, { confirmSelf = false } = {}) => {
    setRevokingId(sess.id);
    try {
      const headers = confirmSelf ? { "X-Confirm-Self-Revoke": "true" } : {};
      await api.post(`/admin/security/sessions/${sess.id}/revoke`, {}, { headers });
      toast.success("Sesiune revocată.");
      reload();
      if (confirmSelf) {
        // Self-revoke killed our cookie — bounce to login.
        setTimeout(() => { window.location.href = "/admin/login"; }, 800);
      }
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Eroare.";
      if (err?.response?.status === 428) {
        // Re-open dialog with confirm-self mode.
        setPendingDialog({ ...sess, requireSelfConfirm: true });
      } else {
        toast.error(msg);
      }
    } finally {
      setRevokingId(null);
    }
  }, [reload]);

  const onRevokeClick = (sess) => {
    setPendingDialog({ ...sess, requireSelfConfirm: sess.is_current });
  };

  const onRevokeAllForUser = useCallback(async (email) => {
    if (!window.confirm(`Revoci TOATE sesiunile pentru ${email}?`)) return;
    try {
      await api.post(`/admin/security/users/${encodeURIComponent(email)}/revoke-all-sessions`, {});
      toast.success("Toate sesiunile utilizatorului au fost revocate.");
      reload();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Eroare.");
    }
  }, [reload]);

  return (
    <SectionCard
      title="Sesiuni"
      icon={Monitor}
      action={
        <div className="flex items-center gap-2">
          <select
            className="text-xs border border-[#E5E7EB] rounded-full px-3 py-1.5 bg-white"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            data-testid="security-sessions-status-filter"
          >
            <option value="active">Active</option>
            <option value="revoked">Revocate</option>
            <option value="expired">Expirate</option>
            <option value="all">Toate</option>
          </select>
          <button
            type="button"
            onClick={reload}
            className="inline-flex items-center gap-1.5 text-xs font-semibold text-[#965EA7] hover:bg-[#F0E7F4] rounded-full px-3 py-1.5"
            data-testid="security-sessions-refresh"
          >
            <RefreshCcw size={12} />
          </button>
        </div>
      }
      testid="security-sessions"
    >
      {loading && <LoadingRows />}
      {error && <div className="text-sm text-[#9b1f2c]">{error}</div>}
      {!loading && !error && (data?.rows?.length === 0 ? (
        <EmptyBlock>Nu există sesiuni în categoria selectată.</EmptyBlock>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="min-w-full text-sm" data-testid="security-sessions-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#6B7280] border-b border-[#E5E7EB]">
                <th className="px-2 py-2 text-left">Utilizator</th>
                <th className="px-2 py-2 text-left">Rol</th>
                <th className="px-2 py-2 text-left">Device</th>
                <th className="px-2 py-2 text-left">IP</th>
                <th className="px-2 py-2 text-left">Ultima activitate</th>
                <th className="px-2 py-2 text-left">Expiră</th>
                {canRevoke && <th className="px-2 py-2 text-right">Acțiuni</th>}
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((row) => (
                <SessionRow
                  key={row.id}
                  row={row}
                  canRevoke={canRevoke}
                  isOwner={isOwner}
                  currentEmail={currentEmail}
                  revoking={revokingId === row.id}
                  onRevoke={() => onRevokeClick(row)}
                  onRevokeAll={() => onRevokeAllForUser(row.email)}
                />
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {pendingDialog && (
        <RevokeConfirmDialog
          session={pendingDialog}
          onCancel={() => setPendingDialog(null)}
          onConfirm={() => {
            const confirmSelf = !!pendingDialog.requireSelfConfirm;
            doRevoke(pendingDialog, { confirmSelf });
            setPendingDialog(null);
          }}
        />
      )}
    </SectionCard>
  );
}

function SuspicionBadges({ suspicion }) {
  const flags = suspicion?.flags || [];
  if (flags.length === 0) return null;
  const labels = {
    new_ip: "IP nou",
    new_device: "Device nou",
    recent_fails: "Multe tentative eșuate",
  };
  return (
    <span className="inline-flex flex-wrap gap-1 ml-2" data-testid="security-suspicion-badges">
      {flags.map((f) => (
        <span
          key={f}
          title={`Atenție: ${labels[f] || f}`}
          className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-bold rounded-full bg-[#FEE2E2] text-[#9b1f2c] border border-[#E63946]/30"
          data-testid={`security-suspicion-${f}`}
        >
          <AlertTriangle size={10} /> {labels[f] || f}
        </span>
      ))}
    </span>
  );
}

function SessionRow({ row, canRevoke, isOwner, currentEmail, revoking, onRevoke, onRevokeAll }) {
  const isCurrent = row.is_current;
  // admin_fped cannot revoke owner sessions; the backend enforces this but we
  // hide the button to avoid friction.
  const targetIsOwner = row.role === "owner";
  const allowedRevoke = canRevoke && (isOwner || !targetIsOwner) && row.status === "active";
  return (
    <tr
      className={`border-b border-[#F5F6F6] ${isCurrent ? "bg-[#F0E7F4]/40" : "hover:bg-[#F5F6F6]/60"}`}
      data-testid={`security-session-row-${row.id}`}
    >
      <td className="px-2 py-2 text-[#373A45]">
        <div className="font-semibold flex items-center">
          {row.email}
          {isCurrent && (
            <span className="ml-2 px-1.5 py-0.5 rounded-full bg-[#965EA7] text-white text-[10px] font-bold">
              SESIUNEA TA
            </span>
          )}
          <SuspicionBadges suspicion={row.suspicion} />
        </div>
        {row.channel && (
          <div className="text-[10px] text-[#6B7280] mt-0.5">via {row.channel}</div>
        )}
      </td>
      <td className="px-2 py-2 text-[#6B7280] uppercase text-[11px] font-semibold">{row.role}</td>
      <td className="px-2 py-2">
        <div className="flex items-center gap-1.5 text-[#373A45]">
          <DeviceIcon device={row.device} />
          <span className="text-xs">{row.device_label || "—"}</span>
        </div>
      </td>
      <td className="px-2 py-2 text-[#373A45] tabular-nums text-xs">
        <span className="inline-flex items-center gap-1">
          <MapPin size={11} className="opacity-60" />
          {row.ip || "—"}
        </span>
      </td>
      <td className="px-2 py-2 text-xs text-[#373A45]">
        <div>{fmtRelative(row.last_seen_at)}</div>
        <div className="text-[10px] text-[#6B7280]">{fmtTs(row.last_seen_at)}</div>
      </td>
      <td className="px-2 py-2 text-xs text-[#6B7280]">{fmtTs(row.expires_at)}</td>
      {canRevoke && (
        <td className="px-2 py-2 text-right">
          {allowedRevoke ? (
            <div className="inline-flex gap-1">
              <button
                type="button"
                onClick={onRevoke}
                disabled={revoking}
                className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#9b1f2c] hover:bg-[#FEE2E2] rounded-full px-2 py-1 transition-colors disabled:opacity-50"
                data-testid={`security-revoke-session-${row.id}`}
              >
                {revoking ? <Loader2 size={11} className="animate-spin" /> : <X size={11} />}
                Revocă
              </button>
              {(isOwner || !targetIsOwner) && (
                <button
                  type="button"
                  onClick={onRevokeAll}
                  className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#6B7280] hover:bg-[#F5F6F6] rounded-full px-2 py-1 transition-colors"
                  title={`Revocă toate sesiunile pentru ${row.email}`}
                  data-testid={`security-revoke-all-${row.email}`}
                >
                  <UserX size={11} /> Toate
                </button>
              )}
            </div>
          ) : (
            <span className="text-[10px] text-[#6B7280] uppercase tracking-widest">—</span>
          )}
        </td>
      )}
    </tr>
  );
}

function RevokeConfirmDialog({ session, onCancel, onConfirm }) {
  const selfWarn = session.requireSelfConfirm;
  return (
    <div className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4">
      <div
        className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6"
        data-testid="security-revoke-dialog"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle size={20} className={selfWarn ? "text-[#E63946]" : "text-[#965EA7]"} />
          <div className="flex-1">
            <h4 className="font-display text-base font-semibold text-[#373A45]">
              {selfWarn ? "Atenție — îți revoci propria sesiune" : "Confirmă revocarea"}
            </h4>
            <p className="text-sm text-[#6B7280] mt-2">
              {selfWarn
                ? "Vei fi delogat imediat și va trebui să te autentifici din nou."
                : `Vrei să revoci sesiunea pentru ${session.email} pe ${session.device_label || "client necunoscut"}?`}
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-semibold text-[#373A45] hover:bg-[#F5F6F6] rounded-full px-4 py-2"
            data-testid="security-revoke-dialog-cancel"
          >
            Renunță
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className={`text-sm font-semibold rounded-full px-4 py-2 text-white ${selfWarn ? "bg-[#E63946] hover:bg-[#c2333e]" : "bg-[#965EA7] hover:bg-[#7A4986]"}`}
            data-testid="security-revoke-dialog-confirm"
          >
            {selfWarn ? "Da, delogheaza-mă" : "Confirmă revocarea"}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// 3) Failed logins table
// ---------------------------------------------------------------------------

function FailedLoginsTable() {
  const [period, setPeriod] = useState("24h");
  const path = `/admin/security/failed-logins?period=${period}&limit=200`;
  const { data, loading, error, reload } = useEndpoint(path);
  return (
    <SectionCard
      title="Tentative de login eșuate"
      icon={ShieldAlert}
      action={
        <div className="flex items-center gap-2">
          <PeriodFilter value={period} onChange={setPeriod} testidPrefix="security-failed" />
          <button
            type="button"
            onClick={reload}
            className="text-xs text-[#965EA7] hover:bg-[#F0E7F4] rounded-full px-2 py-1.5"
            data-testid="security-failed-refresh"
          >
            <RefreshCcw size={12} />
          </button>
        </div>
      }
      testid="security-failed-logins"
    >
      {loading && <LoadingRows />}
      {error && <div className="text-sm text-[#9b1f2c]">{error}</div>}
      {!loading && !error && (data?.rows?.length === 0 ? (
        <EmptyBlock>Nicio tentativă eșuată în perioada selectată.</EmptyBlock>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="min-w-full text-sm" data-testid="security-failed-logins-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#6B7280] border-b border-[#E5E7EB]">
                <th className="px-2 py-2 text-left">Email</th>
                <th className="px-2 py-2 text-left">Motiv</th>
                <th className="px-2 py-2 text-left">Provider</th>
                <th className="px-2 py-2 text-left">IP</th>
                <th className="px-2 py-2 text-left">Data</th>
              </tr>
            </thead>
            <tbody>
              {(data?.rows || []).map((row) => (
                <tr key={row.id} className="border-b border-[#F5F6F6] hover:bg-[#F5F6F6]/60">
                  <td className="px-2 py-2 text-[#373A45]">{row.email || "—"}</td>
                  <td className="px-2 py-2 text-xs text-[#373A45]">
                    {REASON_LABELS[row.reason] || row.reason}
                  </td>
                  <td className="px-2 py-2 text-xs text-[#6B7280] uppercase">{row.provider}</td>
                  <td className="px-2 py-2 text-xs tabular-nums text-[#373A45]">{row.ip || "—"}</td>
                  <td className="px-2 py-2 text-xs text-[#6B7280]">{fmtTs(row.ts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// 4) Audit events table
// ---------------------------------------------------------------------------

function AuditEventsTable() {
  const [period, setPeriod] = useState("24h");
  const path = `/admin/security/audit-events?period=${period}&limit=200`;
  const { data, loading, error, reload } = useEndpoint(path);
  const rows = data?.rows || [];

  const actionTones = useMemo(() => ({
    login_success: "bg-[#E7F2EB] text-[#2F6B4E]",
    failed_login_attempt: "bg-[#FEE2E2] text-[#9b1f2c]",
    auth_session_revoked: "bg-[#FEE2E2] text-[#9b1f2c]",
    auth_all_sessions_revoked: "bg-[#FEE2E2] text-[#9b1f2c]",
    logout: "bg-[#F5F6F6] text-[#373A45]",
    magic_link_invalid: "bg-[#FEE2E2] text-[#9b1f2c]",
    magic_link_rate_limited: "bg-[#FEF3C7] text-[#854D0E]",
    user_invited: "bg-[#E5EEF5] text-[#1f5d80]",
    user_updated: "bg-[#E5EEF5] text-[#1f5d80]",
    user_removed: "bg-[#FEE2E2] text-[#9b1f2c]",
    bootstrap_owner_created: "bg-[#F0E7F4] text-[#7A4986]",
    sessions_revoked: "bg-[#FEE2E2] text-[#9b1f2c]",
    login_denied: "bg-[#FEE2E2] text-[#9b1f2c]",
    login_failed: "bg-[#FEE2E2] text-[#9b1f2c]",
  }), []);

  return (
    <SectionCard
      title="Evenimente Audit (auth)"
      icon={ListChecks}
      action={
        <div className="flex items-center gap-2">
          <PeriodFilter value={period} onChange={setPeriod} testidPrefix="security-audit" />
          <button
            type="button"
            onClick={reload}
            className="text-xs text-[#965EA7] hover:bg-[#F0E7F4] rounded-full px-2 py-1.5"
            data-testid="security-audit-refresh"
          >
            <RefreshCcw size={12} />
          </button>
        </div>
      }
      testid="security-audit-events"
    >
      {loading && <LoadingRows />}
      {error && <div className="text-sm text-[#9b1f2c]">{error}</div>}
      {!loading && !error && (rows.length === 0 ? (
        <EmptyBlock>Niciun eveniment auth în perioada selectată.</EmptyBlock>
      ) : (
        <div className="overflow-x-auto -mx-2">
          <table className="min-w-full text-sm" data-testid="security-audit-events-table">
            <thead>
              <tr className="text-[10px] uppercase tracking-widest text-[#6B7280] border-b border-[#E5E7EB]">
                <th className="px-2 py-2 text-left">Acțiune</th>
                <th className="px-2 py-2 text-left">Actor</th>
                <th className="px-2 py-2 text-left">Țintă</th>
                <th className="px-2 py-2 text-left">IP</th>
                <th className="px-2 py-2 text-left">Detalii</th>
                <th className="px-2 py-2 text-left">Data</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id} className="border-b border-[#F5F6F6] hover:bg-[#F5F6F6]/60">
                  <td className="px-2 py-2">
                    <span
                      className={`inline-block text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-full ${actionTones[row.action] || "bg-[#F5F6F6] text-[#373A45]"}`}
                    >
                      {ACTION_LABELS[row.action] || row.action}
                    </span>
                  </td>
                  <td className="px-2 py-2 text-xs text-[#373A45]">{row.actor_email || "—"}</td>
                  <td className="px-2 py-2 text-xs text-[#6B7280]">{row.target_email || "—"}</td>
                  <td className="px-2 py-2 text-xs tabular-nums text-[#373A45]">{row.ip || "—"}</td>
                  <td className="px-2 py-2 text-[11px] text-[#6B7280] max-w-[280px] truncate" title={JSON.stringify(row.details || {})}>
                    {formatDetails(row.details)}
                  </td>
                  <td className="px-2 py-2 text-xs text-[#6B7280]">{fmtTs(row.ts)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}
    </SectionCard>
  );
}

function formatDetails(details) {
  if (!details || typeof details !== "object" || Object.keys(details).length === 0) return "—";
  return Object.entries(details)
    .filter(([k]) => k !== "ip")  // already shown in its own column
    .map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`)
    .join(" · ");
}
